import { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import toast from "react-hot-toast";
import { useLoaderData, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import "./Cart.css";
import { Typography, Card, Alert } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import { sendSwapOrHireRequest, getBerthOwnerCommissionValue } from "../../../../../services";
import defaultBlogImage from "../../../../../assets/default-blog.png";
import { DateRangeModal } from "../../../../../components";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { useUser } from "../../../../../contexts/UserContext";

const useStyles = makeStyles((theme) => ({
  cont: {
    background: "white",
    width: "100%",
    // maxWidth: "1100px",
    margin: "auto",
    marginTop: "20px",
    borderRadius: "10px",
    overflow: "hidden",
    marginBottom: "20px",
    boxShadow:
      "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
  },

  dealerHeading: {
    fontSize: "24px",
    marginBottom: "24px",
    marginTop: "12px",
  },
  tableContain: {
    padding: "30px",
  },
}));

export const Cart = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_URL;
  const currentTaxRate = process.env.REACT_APP_TAX_RATE || 21;
  const user = useLoaderData();
  const classes = useStyles();
  const { data } = useLocation()?.state;
  const {userData, token, profileComplete, loadingUserData} = useUser()
  // console.log("user: ", user);
  // console.log("cart location: ", data);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [daysCount, setDaysCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [serviceCost, setServiceCost] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [chargeType, setChargeType] = useState("day");
  const [berthOwnerCommissionValue, setBerthOwnerCommissionValue] = useState(null);

  const {
    berthSpecifications: { beam, length, dockHeight, depth },
    currentLocation: { name: location },
    availableFor,
    power,
    portEmail,
    name,
    id,
    userId,
    chargesPerDay,
    chargesPerMonth,
    fixedTotalPrice,
    fixedPeriodEndDate,
    fixedPeriodStartDate,
    berthImagePath,
    availableDate,
    currentlongitude,
    currentLocation,
    currentLatitude,
    bookedDates, // Existing bookings for this berth
  } = data;

  const handleDateRange = (startDate, endDate, pricingType) => {
    const start = dayjs(startDate)
    const end  = dayjs(endDate)

    const diffDays = end.diff(start, 'day')

    setStartDate(start.format("YYYY-MM-DD"));
    setEndDate(end.format("YYYY-MM-DD"));
    setDaysCount(diffDays + 1);
    setChargeType(pricingType);
  };

  useEffect(() => {
    async function getCommissionValue() {

      const commissionValue = await getBerthOwnerCommissionValue(token, data.userId);

      if(commissionValue === null || commissionValue === undefined || typeof commissionValue !== 'number') {
        toast.error("Can not get the service cost for this berth! Please try later");
        return;
      }
      
      setBerthOwnerCommissionValue(commissionValue);
    }

    getCommissionValue();
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  

  useEffect(() => {
    if (daysCount === 0) {
      setTotal(0);
      setServiceCost(0);
      setSubTotal(0);
      return;
    }

    // Base Amount (excluding Commission and Tax amounts)
    let calculatedTotal = 0;

    if (availableFor.toLowerCase() === "swap") {
      calculatedTotal = 0;
    } else {
      // Calculate based on selected pricing type
      switch (chargeType) {
        case 'day':
          if (chargesPerDay && daysCount) {
            calculatedTotal = chargesPerDay * daysCount;
          }
          break;

        case 'month':
          // Monthly pricing: daysCount will always be exactly 30, 60, 90, or 120
          // No remaining days since end date is disabled and only month buttons work
          if (chargesPerMonth && daysCount) {
            calculatedTotal = chargesPerMonth * (daysCount / 30);
          }
          break;

        case 'fixed':
          if (fixedTotalPrice) {
            calculatedTotal = fixedTotalPrice;
          }
          break;

        default:
          calculatedTotal = 0;
      }
    }

    // Ensure calculatedTotal is a valid number before using toFixed
    if (isNaN(calculatedTotal) || !isFinite(calculatedTotal)) {
      calculatedTotal = 0;
    }

    setTotal(calculatedTotal.toFixed(2));
    const serviceCostForBerth = (berthOwnerCommissionValue / 100) * calculatedTotal;
    const totalTaxOnBaseAmount = (currentTaxRate / 100) * calculatedTotal;

    setServiceCost(serviceCostForBerth.toFixed(2));
    setTaxAmount(totalTaxOnBaseAmount.toFixed(2));

    // Actual Total Amount after adding the Base Amount + Commission Value + Tax Amount
    setSubTotal(calculatedTotal + serviceCostForBerth + totalTaxOnBaseAmount);
  }, [daysCount, chargeType, availableFor, chargesPerDay, chargesPerMonth, fixedTotalPrice, berthOwnerCommissionValue, currentTaxRate]);

  const handleRequest = async (type) => {
    if(!profileComplete && !loadingUserData) {
      toast.error("Please complete your profile first");
      return;
    }

    const payload = {
      sourceBerth: {
        id: id,
        name: name,
        berthSpecifications: {
          length: length,
          beam: beam,
          depth: depth,
          dockHeight: dockHeight,
        },
        berthImagePath: berthImagePath,
        portEmail: portEmail,
        currentlongitude,
        currentLocation,
        currentLatitude,
        power: power,
        userId: userId,
      },
      sourceUserId: userId,
      destinationUserId: user?.data?.id,
      destinationUserEmail: user?.data?.userName,
      destinationUserYacthname: user?.data?.yachtName,
      totalAmount: type.toLowerCase() === "swap" ? 0 : subTotal,
      amountDetails: {
        taxAmount: 0,
        serviceCost: type.toLowerCase() === "swap" ? 0 : serviceCost,
        amount: type.toLowerCase() === "swap" ? 0 : total,
      },
      chargeType: chargeType,
      transactionType: type,
      paymentInfo: {},
      bookingDate: { startDate: startDate, endDate: endDate },
    };

    console.log("final: ", payload);
    // return;
    setLoading(true);
    const res = await sendSwapOrHireRequest(payload, user?.token);
    console.log("res: ", res);

    if (res?.status === "Success") {
      toast.success(`${data?.availableFor} request sent successfully`);
      setLoading(false);
      navigate("/home/available-berths");
      return;
    }

    if (res?.status === false) {
      if (res?.message === "Invalid or expired access token.") {
        toast.error("Token expired, please re-login and try");
        setLoading(false);
        return;
      }

      toast.error("Something went wrong please try later");
      setLoading(false);
      return;
    }

    toast.error("Something went wrong please try later");
    setLoading(false);
    return;
  };

  return (
    <>
      <div className={classes.cont}>
        <div className="innerContainer">
          <Grid container spacing={8}>
            <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 7 }}>
              <div className="requestHeaderContainer">
                <Typography variant="h5" component="h4">
                  {name ? name : "--"}
                </Typography>
                <div
                  className={`cardTypeBox ${
                    availableFor
                      ? availableFor === "hire or swap"
                        ? "hireOrSwap"
                        : availableFor
                      : "hireOrSwap"
                  }`}
                >
                  Open to {availableFor ? availableFor : "--"}
                </div>
              </div>
              <Typography mb={2} variant="h6">
                {location ? location : "--"}
              </Typography>
              <Typography variant="subtitle1">
                Berth available from:{" "}
                {(availableDate && availableDate.length > 0)
                  ? dayjs(availableDate[0].startDate).format("YYYY-MM-DD")
                  : "--"}{" "}
                to{" "}
                {(availableDate && availableDate.length > 0)
                  ? dayjs(availableDate[0].endDate).format("YYYY-MM-DD")
                  : "--"}
              </Typography>
              <div className="dateRangeContainer">
                <Button
                  className="btn"
                  variant="contained"
                  onClick={() => setOpen(true)}
                  endIcon={<DateRangeIcon />}
                >
                  Book your slot
                </Button>
                {daysCount > 0 && (
                  <Typography variant="subtitle1">
                    {daysCount} days :{" "}
                    {startDate ? dayjs(startDate).format("YYYY-MM-DD") : "--"}{" "}
                    to {endDate ? dayjs(endDate).format("YYYY-MM-DD") : "--"}
                  </Typography>
                )}
              </div>
              <Grid item sm={12} md={6} lg={4} mt={4}>
                <div className="CartItemContainer">
                  <div className="orderItems ">
                    <div className="firstCartItemContainer">
                      {chargeType === 'day' && (
                        <p>
                          Rate per day €{availableFor.toLowerCase() === "swap" ? 0 : chargesPerDay} × {daysCount} days
                        </p>
                      )}

                      {chargeType === 'month' && (
                        <p>
                          Rate per month €{availableFor.toLowerCase() === "swap" ? 0 : chargesPerMonth} × {daysCount / 30} month{daysCount / 30 > 1 ? 's' : ''} ({daysCount} days)
                        </p>
                      )}

                      {chargeType === 'fixed' && (
                        <p>
                          Fixed Rate €{availableFor.toLowerCase() === "swap" ? 0 : fixedTotalPrice} ({dayjs(fixedPeriodStartDate).format("MMM-DD")} to {dayjs(fixedPeriodEndDate).format("MMM-DD")})
                        </p>
                      )}
                    </div>
                    <p>€{total}</p>
                  </div>
                  <div className="orderItems">
                    <div className="firstCartItemContainer">
                      <p>Tax</p>
                    </div>
                    <p>€{taxAmount}</p>
                  </div>
                  <div className="orderItems">
                    <div className="firstCartItemContainer">
                      <p>Service cost</p>
                    </div>
                    <p>€{serviceCost}</p>
                  </div>
                  <div className="borderTop"></div>
                  <div className="orderItems">
                    <div className="firstCartItemContainer">
                      <p>
                        <b>Sub Total</b>
                      </p>
                    </div>
                    <p>
                      <b>€{subTotal}</b>
                    </p>
                  </div>
                </div>
              </Grid>
              {availableFor.toLowerCase() === "hire or swap" && (
                <div className="note">
                  <Alert severity="info">
                    <strong>Note:</strong> There is no cost associated with berth swap request. Following charges apply only to hire request.
                  </Alert>
                </div>
              )}
              <div className="btnContainer">
                <Button
                  className="cancelBtn"
                  variant="contained"
                  onClick={() => navigate("/home/available-berths")}
                >
                  Cancel
                </Button>
                <div>
                  {availableFor === "hire or swap" ? (
                    <>
                      <Button
                        disabled={subTotal === 0}
                        className="btn"
                        variant="contained"
                        loading={loading}
                        loadingPosition="end"
                        onClick={() => handleRequest("swap")}
                      >
                        Request to swap
                      </Button>{" "}
                      <Button
                        disabled={subTotal === 0}
                        className="btn"
                        variant="contained"
                        loading={loading}
                        loadingPosition="end"
                        onClick={() => handleRequest("hire")}
                      >
                        Request to hire
                      </Button>
                    </>
                  ) : (
                    <Button
                      disabled={
                        (subTotal === 0 && 
                        availableFor.toLowerCase() !== "swap") || 
                        daysCount === 0
                      }
                      className="btn"
                      variant="contained"
                      loading={loading}
                      loadingPosition="end"
                      onClick={() => handleRequest(availableFor)}
                    >
                      Request to {availableFor}
                    </Button>
                  )}
                </div>
              </div>
            </Grid>
            <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
              <div className="cartImageParent">
                <img
                  className="berthCartImage"
                  src={
                    berthImagePath
                      ? `${baseUrl}${berthImagePath}`
                      : defaultBlogImage
                  }
                  alt="berth-image"
                />
              </div>
              <Card>
                <div className="YachtSpecContainer">
                  <Typography variant="subtitle1" gutterBottom>
                    Berth Specifications:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle1">
                        Length: {length ? length : "--"}m
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography variant="subtitle1">
                        Beam: {beam ? beam : "--"}m
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle1">
                        Depth: {depth ? depth : "--"}m
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography variant="subtitle1">
                        Power required: {power ? data?.power : "--"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle1">
                        Dock height: {dockHeight ? dockHeight : "--"}m
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography variant="subtitle1">
                        Port email: {portEmail ? portEmail : "--"}
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
              </Card>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* date range select modal */}
      <DateRangeModal
        open={open}
        handleClose={() => setOpen(false)}
        handleDateRange={handleDateRange}
        availableDates={availableDate}
        fixedPeriodStartDate={fixedPeriodStartDate}
        fixedPeriodEndDate={fixedPeriodEndDate}
        chargeType={chargeType}
        setChargeType={setChargeType}
        rates={({
          chargesPerDay: chargesPerDay,
          chargesPerMonth: chargesPerMonth,
          fixedTotalPrice: fixedTotalPrice,
          fixedPeriodStartDate: fixedPeriodStartDate,
          fixedPeriodEndDate: fixedPeriodEndDate
        })}
        existingBookings={bookedDates}
      />
    </>
  );
};
